/**
 * GraphVisualization Component
 * 
 * Visualizes the room graph structure, showing:
 * - All rooms as nodes
 * - Connections (edges) between rooms
 * - Current player position
 * - Visited rooms (the path taken)
 * - Available vs locked connections
 * 
 * This visualization helps players understand the Hamiltonian Path Problem:
 * - The goal is to visit all nodes (rooms) exactly once
 * - Each edge can only be traversed once (doors lock behind you)
 * - The challenge grows exponentially with more nodes
 */

import { useMemo } from 'react';
import styles from './GraphVisualization.module.scss';

export interface GraphVisualizationProps {
  /** Total number of rooms in the graph */
  roomCount: number;
  /** Adjacency list: Map of room ID to array of connected room IDs */
  edges: Map<number, number[]>;
  /** Set of visited room IDs */
  visited: Set<number>;
  /** Current room ID */
  currentRoom: number;
  /** Solution path (for educational purposes - shown after completion) */
  solutionPath?: number[];
  /** Whether to show the solution path */
  showSolution?: boolean;
}

interface NodePosition {
  x: number;
  y: number;
}

export const GraphVisualization = ({
  roomCount,
  edges,
  visited,
  currentRoom,
  solutionPath,
  showSolution = false,
}: GraphVisualizationProps) => {
  // Calculate node positions in a circular layout
  const nodePositions = useMemo(() => {
    const positions: Map<number, NodePosition> = new Map();
    const centerX = 150;
    const centerY = 150;
    const radius = 120;

    for (let i = 0; i < roomCount; i++) {
      const angle = (i / roomCount) * 2 * Math.PI - Math.PI / 2; // Start from top
      positions.set(i, {
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle),
      });
    }

    return positions;
  }, [roomCount]);

  // Build list of edges to render
  const edgeList = useMemo(() => {
    const list: Array<{ from: number; to: number; visited: boolean }> = [];
    const seen = new Set<string>();

    edges.forEach((neighbors, from) => {
      neighbors.forEach(to => {
        // Create a unique key for this edge (order-independent)
        const key = from < to ? `${from}-${to}` : `${to}-${from}`;
        
        if (!seen.has(key)) {
          seen.add(key);
          // Edge is "visited" if both nodes have been visited
          const edgeVisited = visited.has(from) && visited.has(to);
          list.push({ from, to, visited: edgeVisited });
        }
      });
    });

    return list;
  }, [edges, visited]);

  return (
    <div className={styles.graphVisualization}>
      <svg
        viewBox="0 0 300 300"
        className={styles.svg}
        role="img"
        aria-label="Graph visualization showing room connections"
      >
        {/* Render edges first (so they appear behind nodes) */}
        {edgeList.map(({ from, to, visited: edgeVisited }) => {
          const fromPos = nodePositions.get(from);
          const toPos = nodePositions.get(to);
          
          if (!fromPos || !toPos) return null;

          return (
            <line
              key={`edge-${from}-${to}`}
              x1={fromPos.x}
              y1={fromPos.y}
              x2={toPos.x}
              y2={toPos.y}
              className={`${styles.edge} ${edgeVisited ? styles.edgeVisited : ''}`}
              strokeWidth="2"
            />
          );
        })}

        {/* Render solution path if requested */}
        {showSolution && solutionPath && solutionPath.length > 1 && (
          <g className={styles.solutionPath}>
            {solutionPath.slice(0, -1).map((roomId, index) => {
              const nextRoomId = solutionPath[index + 1];
              if (nextRoomId === undefined) return null;
              
              const fromPos = nodePositions.get(roomId);
              const toPos = nodePositions.get(nextRoomId);
              
              if (!fromPos || !toPos) return null;

              return (
                <line
                  key={`solution-${roomId}-${solutionPath[index + 1]}`}
                  x1={fromPos.x}
                  y1={fromPos.y}
                  x2={toPos.x}
                  y2={toPos.y}
                  className={styles.solutionEdge}
                  strokeWidth="3"
                  strokeDasharray="5,5"
                />
              );
            })}
          </g>
        )}

        {/* Render nodes */}
        {Array.from({ length: roomCount }, (_, i) => i).map(roomId => {
          const pos = nodePositions.get(roomId);
          if (!pos) return null;

          const isVisited = visited.has(roomId);
          const isCurrent = roomId === currentRoom;

          return (
            <g key={`node-${roomId}`}>
              <circle
                cx={pos.x}
                cy={pos.y}
                r="20"
                className={`${styles.node} ${
                  isCurrent ? styles.nodeCurrent : isVisited ? styles.nodeVisited : ''
                }`}
              />
              <text
                x={pos.x}
                y={pos.y}
                className={styles.nodeLabel}
                textAnchor="middle"
                dominantBaseline="central"
              >
                {roomId}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <div className={`${styles.legendIcon} ${styles.legendCurrent}`} />
          <span>Current Room</span>
        </div>
        <div className={styles.legendItem}>
          <div className={`${styles.legendIcon} ${styles.legendVisited}`} />
          <span>Visited</span>
        </div>
        <div className={styles.legendItem}>
          <div className={`${styles.legendIcon} ${styles.legendUnvisited}`} />
          <span>Unvisited</span>
        </div>
      </div>

      {showSolution && (
        <p className={styles.solutionNote}>
          <strong>Solution Path:</strong> {solutionPath?.join(' → ')}
        </p>
      )}
    </div>
  );
};